import { cn } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Users, 
  Video, 
  DollarSign, 
  Activity,
  TrendingUp,
  TrendingDown,
  Clock,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDashboard } from "@/hooks/useDashboard";
import type { DashboardPeriod } from "@/lib/api/dashboard";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown } from "lucide-react";

// Helper component for trend display
function TrendDisplay({ trend, label }: { trend: number; label: string }) {
  const isUp = trend >= 0;
  const isZero = trend === 0;
  
  return (
    <div className={cn(
      "flex items-center text-xs",
      isZero ? "text-muted-foreground" : (isUp ? "text-green-600" : "text-red-600")
    )}>
      {!isZero && (
        isUp ? <TrendingUp className="mr-1 h-3 w-3" /> : <TrendingDown className="mr-1 h-3 w-3" />
      )}
      {isZero ? "No change" : `${isUp ? "+" : ""}${trend}%`}
      <span className="ml-1 text-muted-foreground hidden sm:inline">{label}</span>
    </div>
  );
}

export function DashboardPage() {
  const { data, isLoading, error, refetch, period, setPeriod } = useDashboard();

  const periodLabels: Record<DashboardPeriod, string> = {
    "24h": "Last 24 hours",
    "7d": "Last 7 days",
    "30d": "Last 30 days",
    "90d": "Last 90 days",
    "year": "Last year",
    "all": "All time",
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-[200px]" />
            <Skeleton className="h-4 w-[300px]" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-[120px]" />
            {/* <Skeleton className="h-9 w-[120px]" /> */}
          </div>
        </div>

        {/* System Metrics Grid Skeleton */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-[100px]" />
                <Skeleton className="h-4 w-4 rounded-full" />
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2">
                  <Skeleton className="h-8 w-[100px]" />
                  <Skeleton className="h-4 w-[60px]" />
                </div>
                <Skeleton className="h-3 w-[150px] mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Video Queue Skeleton */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Skeleton className="h-6 w-[150px]" />
                  <Skeleton className="h-4 w-[200px]" />
                </div>
                <Skeleton className="h-9 w-[80px]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex justify-between">
                      <Skeleton className="h-4 w-[100px]" />
                      <Skeleton className="h-4 w-[30px]" />
                    </div>
                    <Skeleton className="h-2 w-full rounded-full" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Subscription Skeleton */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Skeleton className="h-6 w-[200px]" />
                  <Skeleton className="h-4 w-[180px]" />
                </div>
                <Skeleton className="h-9 w-[100px]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex justify-between">
                      <div className="flex gap-2">
                        <Skeleton className="h-4 w-[60px]" />
                        <Skeleton className="h-4 w-[80px]" />
                      </div>
                      <Skeleton className="h-4 w-[50px]" />
                    </div>
                    <Skeleton className="h-2 w-full rounded-full" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <AlertCircle className="h-8 w-8 text-red-500" />
        <p className="text-lg font-medium text-destructive">Failed to load dashboard data</p>
        <Button onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  // Fallback for initial load or empty data
  if (!data) return null;

  const { totalUsers, totalVideos, mrr, activeJobs, videoQueue, subscriptionDistribution } = data;

  // Transform data for UI if needed (e.g. formatting numbers)
  const formatCurrency = (value: number) =>
  new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    maximumFractionDigits: 0,
  }).format(value);

  const systemMetrics = [
    {
      title: "Total Users",
      value: totalUsers.value.toLocaleString(),
      trend: totalUsers.trend,
      trendLabel: totalUsers.trendLabel,
      description: "Active accounts",
      icon: Users,
    },
    {
      title: "Total Videos",
      value: totalVideos.value.toLocaleString(),
      trend: totalVideos.trend,
      trendLabel: totalVideos.trendLabel,
      description: "Generated videos",
      icon: Video,
    },
    {
      title: "MRR",
      value: formatCurrency(mrr.value),
      trend: mrr.trend,
      trendLabel: mrr.trendLabel,
      description: "Monthly recurring revenue",
      icon: DollarSign,
    },
    {
      title: "Active Jobs",
      value: activeJobs.value.toLocaleString(),
      trend: activeJobs.trend,
      trendLabel: activeJobs.trendLabel,
      description: "Currently processing",
      icon: Activity,
    },
  ];

  const totalQueue = videoQueue.pending + videoQueue.processing + videoQueue.completed_24h + videoQueue.failed;

  const getProgress = (value: number) => {
    if (totalQueue === 0) return 0;
    return Math.round((value / totalQueue) * 100);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
          <p className="text-muted-foreground">
            System overview and key metrics
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Clock className="mr-2 h-4 w-4" />
                {periodLabels[period]}
                <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(Object.keys(periodLabels) as DashboardPeriod[]).map((p) => (
                <DropdownMenuItem key={p} onClick={() => setPeriod(p)}>
                  {periodLabels[p]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {/* <Button size="sm">Export Report</Button> */}
        </div>

        </div>
      {/* System Metrics Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {systemMetrics.map((metric) => (
          <Card key={metric.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {metric.title}
              </CardTitle>
              <metric.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <div className="text-2xl font-bold">{metric.value}</div>
                <TrendDisplay trend={metric.trend} label={metric.trendLabel} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {metric.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Video Queue Status */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Video Queue Status</CardTitle>
                <CardDescription>
                  Current video processing pipeline
                </CardDescription>
              </div>
              <Link to="/videos">
                <Button variant="ghost" size="sm">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm">Pending</span>
                </div>
                <span className="font-medium">{videoQueue.pending}</span>
              </div>
              <div className="h-2 w-full rounded-full bg-secondary">
                <div 
                  className="h-2 rounded-full bg-yellow-500" 
                  style={{ width: `${getProgress(videoQueue.pending)}%` }}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">Processing</span>
                </div>
                <span className="font-medium">{videoQueue.processing}</span>
              </div>
              <div className="h-2 w-full rounded-full bg-secondary">
                <div 
                  className="h-2 rounded-full bg-blue-500" 
                  style={{ width: `${getProgress(videoQueue.processing)}%` }}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Completed ({periodLabels[period]})</span>
                </div>
                <span className="font-medium">{videoQueue.completed_24h}</span>
              </div>
              <div className="h-2 w-full rounded-full bg-secondary">
                <div 
                  className="h-2 rounded-full bg-green-500" 
                  style={{ width: `${getProgress(videoQueue.completed_24h)}%` }}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <span className="text-sm">Failed (All time)</span>
                </div>
                <span className="font-medium text-red-600">{videoQueue.failed}</span>
              </div>
              <div className="h-2 w-full rounded-full bg-secondary">
                <div 
                  className="h-2 rounded-full bg-red-500" 
                  style={{ width: `${getProgress(videoQueue.failed)}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Distribution */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>New Subscriptions</CardTitle>
                <CardDescription>
                  By plan ({periodLabels[period]})
                </CardDescription>
              </div>
              <Link to="/subscriptions">
                <Button variant="ghost" size="sm">View Details</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {subscriptionDistribution.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  No new subscriptions in this period
                </div>
              ) : (
                subscriptionDistribution.map((plan) => (
                  <div key={plan.plan} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{plan.plan}</span>
                        <span className="text-xs text-muted-foreground">
                          ({plan.users} users)
                        </span>
                      </div>
                      <span className="text-sm font-medium">{formatCurrency(plan.mrr)}</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-secondary">
                      <div 
                        className="h-2 rounded-full bg-primary" 
                        style={{ width: `${plan.percentage}%` }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity - Kept static for now as per plan focus on overview stats, 
          but could be wired up to an activity log endpoint later if created.
          For now, let's just comment it out or show a simplified placeholder if desired,
          but since we didn't implement 'getRecentActivity' in API, 
          I will keep the mock briefly or remove it to avoid confusion. 
          The task was "get overview data", not full activity log. 
          I'll remove the mock activity log to be clean, 
          or better yet, keep a placeholder note.
       */}
       {/* 
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>To be implemented</CardDescription>
        </CardHeader>
      </Card> 
      */}
    </div>
  );
}

